import React, { useState, useEffect, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import moment from 'moment';
import { BASE_URL } from '../../services/ApiService/ApiConfig';

const API = `${BASE_URL}/reporte-dispositivo`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('jwt')?.replaceAll('"', '') || '';
    return { headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*', 'Access-Control-Allow-Headers': '*', Authorization: 'Bearer ' + token } };
};

const TIPOS = [{ label: 'Matutino (AM)', value: 'AM' }, { label: 'Vespertino (PM)', value: 'PM' }];
const TIPOS_CORREO = [{ label: 'Para (To)', value: 'PARA' }, { label: 'Copia (CC)', value: 'CC' }];

const horarioVacio = { descripcion: '', tipo: 'AM', hora: null, umbralHoras: 2, asunto: '', mensaje: '', activo: true };
const visibilidadVacio = { usuarioIds: [], grupoKeys: [] };
const correoVacio = { grupoKeys: [], correo: '', tipo: 'PARA' };

const ReporteDispositivosConfig = () => {
    const [horarios, setHorarios] = useState([]);
    const [permisos, setPermisos] = useState([]);   // visibilidad usuario<->grupo
    const [correos, setCorreos] = useState([]);      // destinatarios de correo por grupo
    const [grupos, setGrupos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [horarioDialog, setHorarioDialog] = useState(false);
    const [horario, setHorario] = useState(horarioVacio);
    const [visDialog, setVisDialog] = useState(false);
    const [vis, setVis] = useState(visibilidadVacio);
    const [correoDialog, setCorreoDialog] = useState(false);
    const [correo, setCorreo] = useState(correoVacio);

    const toast = useRef(null);

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        // Cada recurso se carga de forma independiente: si uno falla (p.ej. correos antes de
        // aplicar su tabla), no rompe el resto de la configuración.
        const safe = (p) => p.then((r) => r.data).catch(() => null);
        const [h, p, c, g, u] = await Promise.all([
            safe(axios.get(`${API}/horarios`, getAuthHeaders())),
            safe(axios.get(`${API}/permisos`, getAuthHeaders())),
            safe(axios.get(`${API}/correos`, getAuthHeaders())),
            safe(axios.get(`${API}/grupos`, getAuthHeaders())),
            safe(axios.get(`${BASE_URL}/usuario/activos`, getAuthHeaders()))
        ]);
        setHorarios(h || []);
        setPermisos(p || []);
        setCorreos(c || []);
        setGrupos(g || []);
        setUsuarios((u || []).map((it) => ({
            label: `${it.nombre || it.username || ''}`,
            value: it.usuarioId || it.usuario_id || it.id
        })));
    };

    const error = (detail) => toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 3000 });
    const ok = (detail) => toast.current?.show({ severity: 'success', summary: 'OK', detail, life: 2500 });

    const grupoOpciones = grupos.map((g) => ({ label: `${g.nombre} (Traccar ${g.origen})`, value: `${g.origen}|${g.traccarGrupoId}` }));
    const nombreGrupo = (origen, grupoId) => {
        const g = grupos.find((x) => x.origen === origen && x.traccarGrupoId === grupoId);
        return g ? `${g.nombre} (Traccar ${origen})` : `${grupoId} (Traccar ${origen})`;
    };
    const nombreUsuario = (id) => usuarios.find((u) => u.value === id)?.label || id;

    // ---------- HORARIOS ----------
    const nuevoHorario = () => { setHorario({ ...horarioVacio }); setHorarioDialog(true); };
    const editarHorario = (row) => {
        setHorario({ ...row, hora: row.hora ? moment(row.hora, 'HH:mm').toDate() : null });
        setHorarioDialog(true);
    };
    const guardarHorario = async () => {
        if (!horario.hora) { error('Indique la hora.'); return; }
        const payload = { ...horario, hora: moment(horario.hora).format('HH:mm') };
        try {
            if (horario.reporteDispositivoHorarioId) {
                await axios.put(`${API}/horarios/${horario.reporteDispositivoHorarioId}`, payload, getAuthHeaders());
            } else {
                await axios.post(`${API}/horarios`, payload, getAuthHeaders());
            }
            setHorarioDialog(false); ok('Horario guardado.'); cargar();
        } catch (e) { error('No se pudo guardar el horario.'); }
    };
    const horaTpl = (row) => row.hora || '-';
    const activoTpl = (row) => <Tag value={row.activo ? 'Activo' : 'Inactivo'} severity={row.activo ? 'success' : 'danger'} />;
    const accionesHorarioTpl = (row) => <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={() => editarHorario(row)} />;

    // ---------- VISIBILIDAD (usuario <-> grupo) ----------
    const nuevaVis = () => { setVis({ usuarioIds: [], grupoKeys: [] }); setVisDialog(true); };
    const guardarVis = async () => {
        if (!vis.usuarioIds?.length || !vis.grupoKeys?.length) { error('Seleccione al menos un usuario y un grupo.'); return; }
        try {
            const reqs = [];
            for (const uid of vis.usuarioIds) {
                for (const gk of vis.grupoKeys) {
                    const [origen, traccarGrupoId] = gk.split('|');
                    reqs.push(axios.post(`${API}/permisos`, {
                        usuarioId: uid, origen: Number(origen), traccarGrupoId: Number(traccarGrupoId),
                        puedeVer: true, recibeCorreo: false, tipoDestinatario: 'PRINCIPAL', activo: true
                    }, getAuthHeaders()));
                }
            }
            await Promise.all(reqs);
            setVisDialog(false); ok(`${reqs.length} permiso(s) de visibilidad guardado(s).`); cargar();
        } catch (e) { error('No se pudo guardar.'); }
    };
    const eliminarVis = async (row) => {
        try { await axios.delete(`${API}/permisos/${row.reporteDispositivoGrupoUsuarioId}`, getAuthHeaders()); ok('Eliminado.'); cargar(); }
        catch (e) { error('No se pudo eliminar.'); }
    };

    // ---------- CORREOS (cuentas por grupo) ----------
    const nuevoCorreo = () => { setCorreo({ ...correoVacio, grupoKeys: [] }); setCorreoDialog(true); };
    const guardarCorreo = async () => {
        const mail = (correo.correo || '').trim();
        if (!correo.grupoKeys?.length || !mail) { error('Seleccione grupo(s) e ingrese un correo.'); return; }
        // permite varios correos separados por ; o ,
        const mails = mail.split(/[;,]/).map((m) => m.trim()).filter(Boolean);
        try {
            const reqs = [];
            for (const gk of correo.grupoKeys) {
                const [origen, traccarGrupoId] = gk.split('|');
                for (const m of mails) {
                    reqs.push(axios.post(`${API}/correos`, {
                        origen: Number(origen), traccarGrupoId: Number(traccarGrupoId), correo: m, tipo: correo.tipo, activo: true
                    }, getAuthHeaders()));
                }
            }
            await Promise.all(reqs);
            setCorreoDialog(false); ok(`${reqs.length} correo(s) guardado(s).`); cargar();
        } catch (e) { error('No se pudo guardar el correo.'); }
    };
    const eliminarCorreo = async (row) => {
        try { await axios.delete(`${API}/correos/${row.reporteDispositivoGrupoCorreoId}`, getAuthHeaders()); ok('Eliminado.'); cargar(); }
        catch (e) { error('No se pudo eliminar.'); }
    };

    const usuarioTpl = (row) => nombreUsuario(row.usuarioId);
    const grupoTpl = (row) => nombreGrupo(row.origen, row.traccarGrupoId);
    const tipoCorreoTpl = (row) => <Tag value={row.tipo === 'CC' ? 'Copia (CC)' : 'Para (To)'} severity={row.tipo === 'CC' ? 'info' : 'success'} />;
    const accionesVisTpl = (row) => <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm" onClick={() => eliminarVis(row)} />;
    const accionesCorreoTpl = (row) => <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm" onClick={() => eliminarCorreo(row)} />;

    return (
        <div>
            <Toast ref={toast} />
            <TabView>
                <TabPanel header="Horarios de envío">
                    <div className="flex justify-content-end mb-2">
                        <Button label="Nuevo horario" icon="pi pi-plus" className="p-button-sm" onClick={nuevoHorario} />
                    </div>
                    <DataTable value={horarios} responsiveLayout="scroll" emptyMessage="Sin horarios configurados.">
                        <Column field="descripcion" header="Descripción" />
                        <Column field="tipo" header="Tipo" />
                        <Column header="Hora" body={horaTpl} />
                        <Column field="umbralHoras" header="Umbral (h)" />
                        <Column field="asunto" header="Asunto" />
                        <Column header="Estado" body={activoTpl} />
                        <Column header="" body={accionesHorarioTpl} style={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="Visibilidad por grupo">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <small className="text-500">Qué usuarios del sistema pueden ver cada grupo en la pantalla. Si un usuario no tiene ninguno, ve todos.</small>
                        <Button label="Nueva visibilidad" icon="pi pi-plus" className="p-button-sm" onClick={nuevaVis} />
                    </div>
                    <DataTable value={permisos} responsiveLayout="scroll" emptyMessage="Sin reglas de visibilidad.">
                        <Column header="Usuario" body={usuarioTpl} />
                        <Column header="Grupo" body={grupoTpl} />
                        <Column header="" body={accionesVisTpl} style={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="Correos por grupo">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <small className="text-500">Cuentas de correo (sueltas) a las que se envía cada grupo. <b>Para</b> = destinatario; <b>Copia</b> = CC.</small>
                        <Button label="Nuevo correo" icon="pi pi-plus" className="p-button-sm" onClick={nuevoCorreo} />
                    </div>
                    <DataTable value={correos} responsiveLayout="scroll" emptyMessage="Sin correos configurados.">
                        <Column header="Grupo" body={grupoTpl} />
                        <Column field="correo" header="Correo" />
                        <Column header="Tipo" body={tipoCorreoTpl} />
                        <Column header="" body={accionesCorreoTpl} style={{ width: '4rem' }} />
                    </DataTable>
                </TabPanel>
            </TabView>

            {/* Dialog Horario */}
            <Dialog header={horario.reporteDispositivoHorarioId ? 'Editar horario' : 'Nuevo horario'} visible={horarioDialog} style={{ width: '32rem' }} modal onHide={() => setHorarioDialog(false)}
                footer={<><Button label="Cancelar" className="p-button-text" onClick={() => setHorarioDialog(false)} /><Button label="Guardar" icon="pi pi-check" onClick={guardarHorario} /></>}>
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-7"><label>Descripción</label><InputText value={horario.descripcion} onChange={(e) => setHorario({ ...horario, descripcion: e.target.value })} /></div>
                    <div className="field col-12 md:col-5"><label>Tipo</label><Dropdown value={horario.tipo} options={TIPOS} onChange={(e) => setHorario({ ...horario, tipo: e.value })} /></div>
                    <div className="field col-12 md:col-6"><label>Hora</label><Calendar value={horario.hora} onChange={(e) => setHorario({ ...horario, hora: e.value })} timeOnly hourFormat="24" /></div>
                    <div className="field col-12 md:col-6">
                        <label>Umbral (horas) <i className="pi pi-info-circle umbral-help-cfg" style={{ marginLeft: 4, color: '#6366F1', cursor: 'pointer' }} /></label>
                        <Tooltip target=".umbral-help-cfg" position="top" style={{ maxWidth: '340px' }}>
                            <div style={{ lineHeight: 1.5 }}>
                                <b>Umbral (horas):</b> atraso máximo tolerado antes de marcar un equipo como problemático.
                                <br />🟢 <b>OK</b>: desfase dentro del umbral.
                                <br />🟡 <b>Colgado</b>: comunica, pero su último fix GPS está atrasado más que el umbral.
                                <br />🔴 <b>Sin reporte</b>: dejó de comunicar hace más que el umbral.
                            </div>
                        </Tooltip>
                        <InputNumber value={horario.umbralHoras} onValueChange={(e) => setHorario({ ...horario, umbralHoras: e.value })} min={0} suffix=" h" />
                    </div>
                    <div className="field col-12"><label>Asunto del correo</label><InputText value={horario.asunto} onChange={(e) => setHorario({ ...horario, asunto: e.target.value })} /></div>
                    <div className="field col-12"><label>Mensaje del correo</label><InputTextarea value={horario.mensaje} onChange={(e) => setHorario({ ...horario, mensaje: e.target.value })} rows={3} /></div>
                    <div className="field col-12 flex align-items-center gap-2"><Checkbox inputId="hact" checked={horario.activo} onChange={(e) => setHorario({ ...horario, activo: e.checked })} /><label htmlFor="hact" className="mb-0">Activo</label></div>
                </div>
            </Dialog>

            {/* Dialog Visibilidad */}
            <Dialog header="Nueva visibilidad" visible={visDialog} style={{ width: '34rem' }} modal onHide={() => setVisDialog(false)}
                footer={<><Button label="Cancelar" className="p-button-text" onClick={() => setVisDialog(false)} /><Button label="Guardar" icon="pi pi-check" onClick={guardarVis} /></>}>
                <div className="p-fluid formgrid grid">
                    <div className="field col-12"><label>Usuarios</label><MultiSelect value={vis.usuarioIds} options={usuarios} filter display="chip" onChange={(e) => setVis({ ...vis, usuarioIds: e.value })} placeholder="Seleccione uno o varios usuarios" /></div>
                    <div className="field col-12"><label>Grupos</label><MultiSelect value={vis.grupoKeys} options={grupoOpciones} filter display="chip" onChange={(e) => setVis({ ...vis, grupoKeys: e.value })} placeholder="Seleccione uno o varios grupos" /></div>
                    <div className="col-12"><small className="text-500">Estos usuarios verán esos grupos en la pantalla. Se crea una regla por cada usuario × grupo.</small></div>
                </div>
            </Dialog>

            {/* Dialog Correo */}
            <Dialog header="Nuevo correo" visible={correoDialog} style={{ width: '34rem' }} modal onHide={() => setCorreoDialog(false)}
                footer={<><Button label="Cancelar" className="p-button-text" onClick={() => setCorreoDialog(false)} /><Button label="Guardar" icon="pi pi-check" onClick={guardarCorreo} /></>}>
                <div className="p-fluid formgrid grid">
                    <div className="field col-12"><label>Grupos</label><MultiSelect value={correo.grupoKeys} options={grupoOpciones} filter display="chip" onChange={(e) => setCorreo({ ...correo, grupoKeys: e.value })} placeholder="Seleccione uno o varios grupos" /></div>
                    <div className="field col-12 md:col-8"><label>Correo(s)</label><InputText value={correo.correo} onChange={(e) => setCorreo({ ...correo, correo: e.target.value })} placeholder="correo@dominio.com; otro@dominio.com" /></div>
                    <div className="field col-12 md:col-4"><label>Tipo</label><Dropdown value={correo.tipo} options={TIPOS_CORREO} onChange={(e) => setCorreo({ ...correo, tipo: e.value })} /></div>
                    <div className="col-12"><small className="text-500">Podés ingresar varios correos separados por “;”. Se asignan a cada grupo seleccionado.</small></div>
                </div>
            </Dialog>
        </div>
    );
};

export default ReporteDispositivosConfig;
